import { useCreateSculptureEdition } from "@/api/sculpture-edition/sculpture-edition";
import CopyFromMasterField from "@/components/form-items/CopyFromMasterField";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const schema = z.object({
  sculptureEditionNumeroEnregistrement: z.string().min(1, "required"),
});

type FormType = z.infer<typeof schema>;

type AddSculptureEditionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  masterArtworkId: string;
  isFirstChild?: boolean;
  onCreateEdition?: (edition: {
    id: string;
    sculptureEditionNumeroEnregistrement: string;
  }) => void;
};

const AddSculptureEditionModal = ({
  open,
  onOpenChange,
  masterArtworkId,
  isFirstChild = false,
  onCreateEdition,
}: AddSculptureEditionModalProps) => {
  const { t } = useTranslation();
  const [copyFromMaster, setCopyFromMaster] = useState(false);
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      sculptureEditionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync, isPending } = useCreateSculptureEdition();

  const onFormSubmit = async (data: FormType) => {
    try {
      const created = await mutateAsync({
        masterArtworkId,
        sculptureEditionNumeroEnregistrement:
          data.sculptureEditionNumeroEnregistrement,
        sculptureEditionPublicationStatus: "draft",
        copyFromMaster: isFirstChild && copyFromMaster,
      });

      onCreateEdition?.({
        id: created.id,
        sculptureEditionNumeroEnregistrement:
          created.sculptureEditionNumeroEnregistrement,
      });
      onOpenChange(false);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("sculptureEditionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("sculpture.form.addSculptureEdition")}
      onFormSubmit={onFormSubmit}
      form={form}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="sculptureEditionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("sculpture.form.sculptureEditionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isFirstChild ? (
        <CopyFromMasterField
          checked={copyFromMaster}
          onCheckedChange={setCopyFromMaster}
        />
      ) : null}
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default AddSculptureEditionModal;
